// @ts-nocheck

import { createSelector } from 'reselect';

import { selectMatchUnachivedMissionId } from '../../modules/missions/selectors/banner';
import { getMatchIdFromContextFromProps, getMatchIdFromProps } from '../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityCategoryId,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityStatus,
  selectEntitiesMatchesEntityStoppageTimeAnnounced,
  selectEntitiesMatchesEntityTournamentId,
  selectEntitiesMatchesEntityTvChannels,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectFavoritesIds } from '../../selectors/favorites/favoritesPrimary.selector';
import {
  selectMatchFlagId,
  selectMatchViewMode,
  selectMatchCategoriesEntityCategoryName,
  selectMatchCategoriesEntityWarning,
  selectMatchCompetitor1NbRedCards,
  selectMatchCompetitor2NbRedCards,
  selectMatchSportsEntitySportName,
  selectMatchSportsEntityWarning,
  selectMatchTournamentsEntityTopName,
  selectMatchTournamentsEntityTournamentName,
  selectMatchTournamentsEntityWarning,
} from '../../selectors/match.selector';
import { selectRuntimeIsStreamHighlightsEnabled } from '../../selectors/runtime/runtimePrimary.selector';
import { selectUserConnected } from '../../selectors/user/userPrimary.selector';
import { MatchEntity } from '../../states/entities/matches';
import { MatchViewModes } from '../../types/matchView';
import PeriodIds from '../../types/periodIds';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

const selectIsFavorite = createSelector(
  [getMatchIdFromProps, selectFavoritesIds],
  (matchId, favoriteMatchIds) => !!~favoriteMatchIds.indexOf(matchId),
);

const selectStoppageTimeAnnouncedNbMins = createSelector(
  [selectEntitiesMatchesEntityStoppageTimeAnnounced],
  (stoppageTimeAnnounced) => {
    if (typeof stoppageTimeAnnounced === 'string') {
      const [mins] = stoppageTimeAnnounced.split(':').reduce((acc, k) => (k ? [...acc, parseInt(k, 10)] : acc), []);

      if (typeof mins === 'number') {
        return mins;
      }
    }
  },
);

const selectShouldHideTimerInfos = createSelector(
  selectEntitiesMatchesEntity,
  (match) =>
    !!~[
      PeriodIds.PERIOD_ID_SOCCER_HALF_TIME,
      PeriodIds.PERIOD_ID_SOCCER_END_REGULAR_TIME,
      PeriodIds.PERIOD_ID_SOCCER_OVERTIME_HALF_TIME,
      PeriodIds.PERIOD_ID_SOCCER_BEFORE_PSO,
      PeriodIds.PERIOD_ID_PSO_START,
    ].indexOf(match?.periodId),
);

const selectMatchCompetitionRedirection = createSelector(
  [selectEntitiesMatchesEntitySportId, selectEntitiesMatchesEntityCategoryId, selectEntitiesMatchesEntityTournamentId],
  (sportId, categoryId, tournamentId) => {
    if (sportId && categoryId && tournamentId) {
      return RoutesRedirectionFactory.getCatalogRoutePathname(sportId, categoryId, tournamentId);
    }
    return null;
  },
);

const selectMyMatchViewModeIsAvailable = createSelector(
  [selectMatchViewMode, getMatchIdFromContextFromProps],
  (matchViewMode, matchIdFromContext) => matchViewMode === MatchViewModes.MY_MATCH && !!matchIdFromContext,
);

const selectMatchHeaderProps = createSelector(
  [
    selectEntitiesMatchesEntity,
    selectMatchCompetitionRedirection,
    selectUserConnected,
    selectEntitiesMatchesEntityTvChannels,
    selectEntitiesMatchesEntityStatus,
    selectIsFavorite,
    selectMatchCompetitor1NbRedCards,
    selectMatchCompetitor2NbRedCards,
    selectMatchFlagId,
    selectMatchUnachivedMissionId,
    selectMatchSportsEntitySportName,
    selectMatchSportsEntityWarning,
    selectMatchCategoriesEntityCategoryName,
    selectMatchCategoriesEntityWarning,
    selectMatchTournamentsEntityTopName,
    selectMatchTournamentsEntityTournamentName,
    selectMatchTournamentsEntityWarning,
    selectRuntimeIsStreamHighlightsEnabled,
    selectStoppageTimeAnnouncedNbMins,
    selectShouldHideTimerInfos,
    selectMyMatchViewModeIsAvailable,
  ],
  (
    match,
    subtitleRedirection,
    isUserConnected,
    tvChannels,
    status,
    isFavorite,
    competitor1NbRedCards,
    competitor2NbRedCards,
    flagId,
    missionId,
    sportName,
    sportWarning,
    categoryName,
    categoryWarning,
    tournamentTopName,
    tournamentName,
    tournamentWarning,
    streamHighlightsEnabled,
    stoppageTimeAnnouncedNbMins,
    shouldHideTimerInfos,
    myMatchViewModeIsAvailable,
  ) => ({
    ...match,
    subtitleRedirection,
    tvChannels: tvChannels,
    status: status?.toLowerCase?.(),

    sportName: sportName,
    sportWarning: sportWarning,

    categoryName: categoryName,
    categoryWarning: categoryWarning,

    topName: tournamentTopName,
    tournamentName: tournamentName,
    tournamentWarning: tournamentWarning,

    flagId,
    missionId,
    competitor1NbRedCards,
    competitor2NbRedCards,

    showFavorite: isUserConnected,
    isMultimatch: MatchEntity.isMultimatch(match),
    isFavorite,
    streamHighlightsAvailable: streamHighlightsEnabled && MatchEntity.hasHighlights(match),
    stoppageTimeAnnouncedNbMins,
    shouldHideTimerInfos,
    myMatchViewModeIsAvailable,
  }),
);

export default selectMatchHeaderProps;
